/**
 * The handoff screen (D170): the home host, asked by a creator's domain to
 * sign a player in and send them back.
 *
 * @remarks
 * A page on `play.theirgame.com` cannot see the account's cookie; this
 * page can. It arrives with `?handoff=<return>`, and does three things in
 * order, each of them once: it finds out who is signed in — offering the
 * account dialog, open, when nobody is; it checks the return address
 * against the host records (D162's `GET /hosts/:hostname`), so a token
 * leaves only for a hostname we serve; and it mints the one-time token and
 * navigates to the return with it in the fragment. The player sees this
 * page for as long as that takes, which, signed in, is a moment.
 *
 * The sign-in doors reload the page (the shell's rule since D157: a new
 * owner opens again from the top), and the ask survives the reload because
 * `bootSession` keeps it in every door's callback — so a player who signed
 * in by magic link lands back here, signed in, and is sent on.
 *
 * D176, from Calder's read of the deployed page: the one page whose whole
 * job is to not look like a phishing redirect was the least-branded thing
 * we shipped, and its doors were a modal over an empty page with a second
 * "Sign in" button behind it. So the engine's own masthead stands over it
 * — one identity across every game is the engine speaking about itself,
 * the same moment the boot and a failed boot are — and the doors are drawn
 * inline under the page's heading: signing in IS the page.
 *
 * @packageDocumentation
 */

import { useEffect, useState } from 'react';

import { AccountControl } from './account-control.js';
import { HANDOFF_NOTICE } from './account-copy.js';
import { handoffReturnUrl } from './handoff.js';
import { type IdentityClient } from './identity-client.js';
import { EngineMastheadComponent } from './masthead-component.js';

/** Props for {@link HandoffScreen}. */
export interface HandoffScreenProps {
	readonly identity: IdentityClient;
	/** Whether the return hostname has a host record — the play Worker's door, or a test's answer. */
	readonly isServedHostname: (hostname: string) => Promise<boolean>;
	/** Where the browser goes with the token. Defaults to the real navigation; a test hands in a recorder. */
	readonly navigate?: (url: string) => void;
	/** The doors that change who is signed in reload the page (the shell's rule). */
	readonly onIdentityChanged: () => void;
	/** The address this page was asked to hand a signed-in player back to. */
	readonly returnUrl: string;
}

/** What the screen is doing, in order. */
type Step =
	| { readonly kind: 'asking' }
	| { readonly kind: 'refused'; readonly line: string }
	| { readonly kind: 'sent' }
	| { readonly kind: 'signed-out' };

/** The real navigation: the browser leaves for the creator's page, token in hand. */
const goToPage = (url: string): void => {
	window.location.assign(url);
};

/**
 * Sign in for a creator's domain and send the player back.
 *
 * @param props - {@link HandoffScreenProps}
 * @returns The screen
 */
export const HandoffScreen = ({ identity, isServedHostname, navigate = goToPage, onIdentityChanged, returnUrl }: HandoffScreenProps) => {
	const [step, setStep] = useState<Step>({ kind: 'asking' });
	const returnHostname = new URL(returnUrl).hostname;

	useEffect(() => {
		let isDisposed = false;
		const settle = (next: Step) => {
			if (!isDisposed) {
				setStep(next);
			}
		};

		const proceed = async (): Promise<void> => {
			const account = await identity.readSession();

			if (account === undefined) {
				settle({ kind: 'signed-out' });

				return;
			}

			// The return is checked BEFORE the mint: a token is never made for
			// an address it will not be sent to.
			if (!(await isServedHostname(returnHostname))) {
				settle({ kind: 'refused', line: HANDOFF_NOTICE.notServed(returnHostname) });

				return;
			}

			const token = await identity.mintHandoff();

			if (token === undefined) {
				settle({ kind: 'refused', line: HANDOFF_NOTICE.notMinted });

				return;
			}

			settle({ kind: 'sent' });
			navigate(handoffReturnUrl(returnUrl, token));
		};

		proceed().catch(() => {
			settle({ kind: 'refused', line: HANDOFF_NOTICE.notMinted });
		});

		return () => {
			isDisposed = true;
		};
	}, [identity, isServedHostname, navigate, returnHostname, returnUrl]);

	return (
		<div className="handoff-page">
			<EngineMastheadComponent />
			<section aria-labelledby="handoff-heading" className="handoff">
				<h2 className="handoff__heading" id="handoff-heading">
					{HANDOFF_NOTICE.title}
				</h2>
				<p className="handoff__lead">{HANDOFF_NOTICE.homeLead(returnHostname)}</p>
				{step.kind === 'refused' ? (
					<p className="handoff__notice" role="alert">
						{step.line}
					</p>
				) : undefined}
				{step.kind === 'signed-out' ? (
					<AccountControl arrivalNotice={undefined} arrivalSearch="" identity={identity} onIdentityChanged={onIdentityChanged} presentation="inline" />
				) : undefined}
			</section>
		</div>
	);
};
